import { t } from '@lingui/core/macro'
import { useEffect, useMemo, useState } from 'react'
import ECharts from '~/components/echarts'
import {
  organizationAWSEC2InstancePath,
  organizationAWSEKSDashboardClusterCpuCapacityChartPath,
  organizationAWSEKSDashboardClusterMemoryCapacityChartPath,
  organizationAWSEKSDashboardClusterNodegroupsPath,
  organizationAWSEKSDashboardClusterNoNodegroupNodesPath,
  organizationAWSEKSDashboardNodegroupNodesPath,
} from '~/routes'

export default function AWSEKSClusterCapacityCharts({ organizationId: orgId, clusterId }) {
  const [nodegroups, setNodegroups] = useState([])
  const [nodes, setNodes] = useState([])
  const [selectedNodegroup, setSelectedNodegroup] = useState('')
  const [selectedNode, setSelectedNode] = useState('')
  const [chartParams, setChartParams] = useState({})

  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  useEffect(() => {
    fetchNodegroups()
  }, [clusterId])

  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  useEffect(() => {
    if (selectedNodegroup === 'no-nodegroup') {
      fetchNoNodegroupNodes()
    } else if (selectedNodegroup) {
      fetchNodes(selectedNodegroup)
    } else {
      setNodes([])
      setSelectedNode('')
    }
  }, [selectedNodegroup])

  useEffect(() => {
    const params = {}
    if (selectedNodegroup === 'no-nodegroup') {
      params.no_nodegroup = true
    } else if (selectedNodegroup) {
      params.nodegroup_id = selectedNodegroup
    }
    if (selectedNode) {
      params.node_id = selectedNode
    }
    setChartParams(params)
  }, [selectedNodegroup, selectedNode])

  const fetchNodegroups = async () => {
    try {
      const response = await fetch(organizationAWSEKSDashboardClusterNodegroupsPath(orgId, clusterId))
      const data = await response.json()
      setNodegroups(data)
    } catch (error) {
      console.error('Error fetching nodegroups:', error)
    }
  }

  const fetchNodes = async (nodegroupId) => {
    try {
      const url = organizationAWSEKSDashboardNodegroupNodesPath(orgId, nodegroupId)

      const response = await fetch(url)
      const data = await response.json()
      setNodes(data)
    } catch (error) {
      console.error('Error fetching nodes:', error)
      setNodes([])
    }
  }

  const fetchNoNodegroupNodes = async () => {
    try {
      const response = await fetch(organizationAWSEKSDashboardClusterNoNodegroupNodesPath(orgId, clusterId))
      const data = await response.json()
      setNodes(data)
    } catch (error) {
      console.error('Error fetching no-nodegroup nodes:', error)
      setNodes([])
    }
  }

  const handleNodegroupChange = (e) => {
    const value = e.target.value
    setSelectedNodegroup(value)
    setSelectedNode('')
  }

  const handleNodeChange = (e) => {
    setSelectedNode(e.target.value)
  }

  const cpuCapacityChartUrl = useMemo(
    () => organizationAWSEKSDashboardClusterCpuCapacityChartPath(orgId, clusterId),
    [orgId, clusterId],
  )

  const memoryCapacityChartUrl = useMemo(
    () => organizationAWSEKSDashboardClusterMemoryCapacityChartPath(orgId, clusterId),
    [orgId, clusterId],
  )

  return (
    <div className='card mb-4'>
      <div className='card-body'>
        <div className='row mb-3'>
          <div className='col-md-12'>
            <div className='d-flex gap-3 align-items-center'>
              <div className='flex-grow-1'>
                <label htmlFor='nodegroup-filter' className='form-label mb-1'>
                  {t`Filter by Nodegroup`}
                </label>
                <select
                  id='nodegroup-filter'
                  className='form-select'
                  value={selectedNodegroup}
                  onChange={handleNodegroupChange}
                >
                  <option value=''>{t`All Nodegroups`}</option>
                  {nodegroups.map((ng) => (
                    <option key={ng.id} value={ng.id}>
                      {ng.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedNodegroup && (
                <div className='flex-grow-1 position-relative'>
                  <label htmlFor='node-filter' className='form-label mb-1'>
                    {t`Filter by Node`}
                  </label>
                  <select id='node-filter' className='form-select' value={selectedNode} onChange={handleNodeChange}>
                    <option value=''>{t`All Nodes`}</option>
                    {nodes.map((node) => (
                      <option key={node.id} value={node.id}>
                        {node.aws_instance_id}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const selected = nodes.find((n) => String(n.id) === String(selectedNode))
                    if (!selected?.ec2_instance_id) return null
                    return (
                      <a
                        className='small position-absolute start-0 mt-1'
                        style={{ top: '100%' }}
                        href={organizationAWSEC2InstancePath(orgId, selected.ec2_instance_id)}
                      >
                        {t`View EC2 instance`}
                      </a>
                    )
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='row gy-4'>
          <div className='col-sm-6'>
            <ECharts
              title={t`CPU Provisioned vs Used`}
              url={cpuCapacityChartUrl}
              params={chartParams}
              showExportButton
            />
          </div>
          <div className='col-sm-6'>
            <ECharts
              title={t`Memory Provisioned vs Used`}
              url={memoryCapacityChartUrl}
              params={chartParams}
              showExportButton
            />
          </div>
        </div>
      </div>
    </div>
  )
}
